import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AudioFileService from '../services/AudioFileService';
import VideoCRUDService from '../services/VideoCRUDService';

const VideoProcessingContext = createContext();

export const useVideoProcessing = () => useContext(VideoProcessingContext);

export const VideoProcessingProvider = ({ children }) => {
  // Use an empty array for production
  const [processingVideos, setProcessingVideos] = useState([]);
  
  // Track all polling intervals to clean them up
  const pollingIntervalsRef = useRef({});
  
  // Clean up function to remove completed videos
  const cleanupCompletedVideos = useCallback(() => {
    setProcessingVideos(prev => 
      prev.filter(video => {
        // Keep videos that are still processing
        const isAudio = video.type === 'audio';
        const isComplete = isAudio
          ? ['COMPLETED', 'FAILED', 'SKIPPED'].includes(video.dubbing)
          : (video.compression === 'COMPLETED' || video.compression === 'FAILED') &&
            (video.translation === 'COMPLETED' || video.translation === 'FAILED' || video.translation === 'SKIPPED');
        
        // If complete for more than 5 minutes, remove it
        if (isComplete && video.lastUpdated) {
          const fiveMinutesAgo = new Date();
          fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
          return new Date(video.lastUpdated) > fiveMinutesAgo;
        }
        
        return true;
      })
    );
  }, []);
  
  // Poll for a specific video's status
  const pollVideoStatus = useCallback(async (videoId, itemType = 'video') => {
    console.log(`Polling status for ${itemType} ${videoId}`);
    
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const authToken = JSON.parse(localStorage.getItem('authToken') || '""');
      
      if (!authUser?.email) {
        console.log('No authenticated user, skipping poll');
        return;
      }
      
      if (itemType === 'audio') {
        const audioData = await AudioFileService.getAudioFileById(videoId);
        if (audioData) {
          const audioStatus = audioData.dubbingStatus || 'NOT_STARTED';
          setProcessingVideos((prev) =>
            prev.map((video) =>
              video.id === videoId
                ? {
                    ...video,
                    ...audioData,
                    type: 'audio',
                    dubbing: audioStatus,
                    compression: 'SKIPPED',
                    translation: 'SKIPPED',
                    languages: [
                      {
                        name: audioData.sourceLanguage || 'Audio',
                        status: 'READY',
                        dubbingStatus: audioStatus,
                        dubbingJobId: audioData.dubbingJobId,
                        dubbingSourceUrl: audioData.dubbingSourceUrl,
                        dubbingTransformedUrl: audioData.dubbingTransformedUrl,
                      },
                    ],
                    lastUpdated: new Date(),
                  }
                : video,
            ),
          );

          if (['COMPLETED', 'FAILED', 'SKIPPED'].includes(audioStatus)) {
            clearInterval(pollingIntervalsRef.current[videoId]);
            delete pollingIntervalsRef.current[videoId];
          }
        }
        return;
      }

      const response = await VideoCRUDService.getVideoProcessingStatus(videoId, authUser.email, authToken);
      
      if (response && response.data) {
        console.log(`Got status update for ${videoId}:`, response.data);
        
        // Normalize backend videoLanguages into the UI-friendly `languages` shape (use dubbingStatus returned by backend)
        const videoData = response.data;
        if (Array.isArray(videoData.videoLanguages)) {
          videoData.languages = videoData.videoLanguages.map((l) => ({
            name: l.languageName || l.name,
            status: l.status || l.languageStatus || l.processStatus || l.status,
            dubbingJobId: l.dubbingJobId,
            dubbingStatus: l.dubbingStatus,
            dubbingTransformedUrl: l.dubbingTransformedUrl,
            dubbingAudioUrl: l.dubbingAudioUrl,
            dubbingSourceUrl: l.dubbingSourceUrl,
            dubbingSourceSharepointUrl: l.dubbingSourceSharepointUrl,
          }));
        }

        const hasDubbing = Array.isArray(videoData.languages) && videoData.languages.some((l) => l.dubbingJobId || l.dubbingStatus);
        if (hasDubbing) {
          const statuses = videoData.languages.map((l) => l.dubbingStatus).filter(Boolean);
          if (statuses.some((s) => s === 'PROCESSING')) {
            videoData.dubbing = 'PROCESSING';
          } else if (statuses.some((s) => s === 'FAILED')) {
            videoData.dubbing = 'FAILED';
          } else if (statuses.length > 0 && statuses.every((s) => ['COMPLETED', 'SKIPPED', 'NOT_STARTED'].includes(s))) {
            videoData.dubbing = 'COMPLETED';
          } else {
            videoData.dubbing = 'NOT_STARTED';
          }
        } else {
          videoData.dubbing = 'NOT_STARTED';
        }

        // Update just this video in the state
        setProcessingVideos((prev) =>
          prev.map((video) => (video.id === videoId ? { ...video, ...videoData, type: video.type || 'video', lastUpdated: new Date() } : video)),
        );

        // Determine if any dubbing work exists and whether it's completed
        let dubbingCompleted = true;
        if (hasDubbing) {
          dubbingCompleted = videoData.languages.every((l) => ['COMPLETED', 'FAILED', 'SKIPPED', 'NOT_STARTED'].includes(l.dubbingStatus));
        }

        // Stop polling only when compression+translation are terminal AND dubbing (if present) is also finished
        if (
          (videoData.compression === 'COMPLETED' || videoData.compression === 'FAILED') &&
          (videoData.translation === 'COMPLETED' || videoData.translation === 'FAILED' || videoData.translation === 'SKIPPED') &&
          (!hasDubbing || dubbingCompleted)
        ) {
          console.log(`Video ${videoId} is complete, stopping polling`);
          clearInterval(pollingIntervalsRef.current[videoId]);
          delete pollingIntervalsRef.current[videoId];
        }
      }
    } catch (err) {
      console.error(`Error polling status for video ${videoId}:`, err);
    }
  }, []);
  
  // Start polling for a specific video
  const startPolling = useCallback((videoId, itemType = 'video') => {
    console.log(`Starting polling for ${itemType} ${videoId}`);
    
    // Clear any existing polling for this video
    if (pollingIntervalsRef.current[videoId]) {
      clearInterval(pollingIntervalsRef.current[videoId]);
    }
    
    // Poll immediately first
    pollVideoStatus(videoId, itemType);
    
    // Then poll every 5 seconds
    const intervalId = setInterval(() => pollVideoStatus(videoId, itemType), 60000);
    pollingIntervalsRef.current[videoId] = intervalId;
    
    return () => {
      clearInterval(intervalId);
      delete pollingIntervalsRef.current[videoId];
    };
  }, [pollVideoStatus]);
  
  // Add a new video to track
  const trackVideo = useCallback((videoId, videoName, skipTranslation = false, itemType = 'video') => {
    console.log(`Tracking new video: ${videoId} - ${videoName}`);
    
    if (!videoId) {
      console.error('Cannot track video: No videoId provided');
      return;
    }
    
    // First, check if we're already tracking this video
    setProcessingVideos(prev => {
      const alreadyTracking = prev.some(v => v.id === videoId);
      if (alreadyTracking) {
        console.log(`Already tracking ${videoId}, restarting polling and updating name`);
        // Reset compression/translation to PENDING so the UI immediately shows
        // "in progress" rather than keeping any stale COMPLETED state from the
        // previous job.  The poller will overwrite these with real values.
        setProcessingVideos(current =>
          current.map((v) =>
            v.id === videoId
              ? {
                  ...v,
                  name: videoName || v.name,
                  skipTranslation: skipTranslation || v.skipTranslation,
                  type: itemType || v.type,
                  compression: 'PENDING',
                  translation: 'PENDING',
                }
              : v,
          ),
        );

        // Restart polling to ensure status checks resume
        try {
          startPolling(videoId, itemType || 'video');
        } catch (e) {
          console.warn('Failed to restart polling for', videoId, e);
        }

        return prev;
      }

      console.log(`Adding ${videoId} to tracking list`);
      const newVideo = {
        id: videoId,
        name: videoName || 'Unnamed Video',
        type: itemType,
        skipTranslation: skipTranslation,
        addedAt: new Date(),
        compression: itemType === 'audio' ? 'SKIPPED' : 'PENDING',
        translation: itemType === 'audio' ? 'SKIPPED' : skipTranslation ? 'SKIPPED' : 'PENDING',
        dubbing: itemType === 'audio' ? 'PROCESSING' : 'NOT_STARTED',
        expanded: false, // Collapsed by default
        languages: []
      };

      // Start polling for this video's status
      setTimeout(() => startPolling(videoId, itemType), 500);

      return [...prev, newVideo];
    });
  }, [startPolling]);
  
  // Remove a video from tracking
  const removeVideo = useCallback((videoId) => {
    console.log(`Removing video ${videoId} from tracking`);
    
    // Clear any polling intervals for this video
    if (pollingIntervalsRef.current[videoId]) {
      clearInterval(pollingIntervalsRef.current[videoId]);
      delete pollingIntervalsRef.current[videoId];
    }
    
    setProcessingVideos(prev => prev.filter(v => v.id !== videoId));
  }, []);
  
  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);
  
  // Run cleanup periodically
  useEffect(() => {
    const cleanupInterval = setInterval(cleanupCompletedVideos, 60000); // Check every minute
    return () => clearInterval(cleanupInterval);
  }, [cleanupCompletedVideos]);
  
  // Toggle expanded state for a video
  const toggleExpanded = useCallback((videoId) => {
    setProcessingVideos(prev => 
      prev.map(video => 
        video.id === videoId 
          ? { ...video, expanded: !video.expanded }
          : video
      )
    );
  }, []);
  
  // Calculate completion percentage (include dubbing when present)
  const getCompletionPercentage = useCallback((video) => {
    if (video.type === 'audio') {
      if (['COMPLETED', 'FAILED', 'SKIPPED'].includes(video.dubbing)) return 100;
      if (video.dubbing === 'PROCESSING') return 50;
      return 0;
    }

    let total = 2; // Compression + Translation
    let completed = 0;

    if (video.compression === 'COMPLETED') completed++;
    if (video.translation === 'COMPLETED' || video.translation === 'SKIPPED') completed++;

    // If dubbing exists for this video (per-language), include it as a stage
    const hasDubbing = video.languages && video.languages.some((l) => l.dubbingJobId || l.dubbingStatus);
    if (hasDubbing) {
      total++;
      const dubbingLangs = video.languages.filter((l) => l.dubbingJobId || l.dubbingStatus);
      const dubbingCompleted =
        dubbingLangs.length > 0 && dubbingLangs.every((l) => l.dubbingStatus === 'COMPLETED' || l.dubbingStatus === 'SKIPPED' || l.dubbingStatus === 'FAILED' || l.dubbingStatus === 'NOT_STARTED');
      if (dubbingCompleted) completed++;
    }

    return Math.round((completed / total) * 100);
  }, []);
  
  return (
    <VideoProcessingContext.Provider 
      value={{ 
        processingVideos,
        trackVideo,
        removeVideo,
        toggleExpanded,
        getCompletionPercentage
      }}
    >
      {children}
    </VideoProcessingContext.Provider>
  );
};